import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Phone, ArrowRight, Star, MapPin, BookOpen, Users,
  GraduationCap, ClipboardCheck, CheckCircle2, Clock
} from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '@/components/ui/accordion';
import LeadForm from '@/components/LeadForm';

const WA = 'https://wa.me/919930010800?text=Hi%20Planet%20Gyaan!%20I%27m%20looking%20for%20CBSE%20classes%20in%20Santacruz%20West.';
const TEL = 'tel:+919930010800';
const LOGO = 'https://customer-assets.emergentagent.com/job_planet-gyaan-edu/artifacts/no8w3qt3_Untitled%20design-73.png';

const WaIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const highlights = [
  { icon: BookOpen, title: 'Mathematics & Science', desc: 'Specialized CBSE coaching in Math, Physics, Chemistry, and Biology.' },
  { icon: Users, title: 'Small Batches (8-10)', desc: 'Personal attention in every class. No student gets left behind.' },
  { icon: GraduationCap, title: 'Expert CBSE Faculty', desc: 'Experienced teachers who know the CBSE curriculum inside out.' },
  { icon: ClipboardCheck, title: 'Regular Testing', desc: 'Weekly assessments, mock exams, and performance tracking.' },
  { icon: Clock, title: 'Flexible Timings', desc: 'Morning, afternoon, and evening batches. Virtual + Offline options.' },
  { icon: MapPin, title: 'Santacruz West Location', desc: 'Tirupati Shopping Center, Swami Vivekanand Rd. Easy to reach.' },
];

const testimonials = [
  { name: 'Arjun S.', grade: 'Grade 12, CBSE', text: 'Best CBSE coaching in Santacruz! Scored 97% in Math board exam.', score: '97%' },
  { name: 'Priya M.', grade: 'Grade 10, CBSE', text: 'The teachers explain concepts so clearly. My Science marks jumped from 75% to 93%.', score: '93%' },
  { name: 'Rahul K.', grade: 'Grade 11, CBSE', text: 'Planet Gyaan helped me prepare for both boards and JEE simultaneously. Highly recommend!', score: '92%' },
];

const faqs = [
  { q: 'Where are your CBSE classes located in Santacruz West?', a: 'We are located at Ground floor, Dastoor Store, Tirupati Shopping Center, 8, Swami Vivekanand Rd, near Riyaasat Store, opp. Kora and Phiroze, Willingdon, Santacruz (West), Mumbai.' },
  { q: 'Which CBSE grades do you offer classes for?', a: 'We offer classes for CBSE students from Grade 8 to Grade 12, covering Mathematics and Science subjects.' },
  { q: 'Do you offer both online and offline CBSE classes?', a: 'Yes! We offer both virtual (online) and offline CBSE classes at our Santacruz West center. You can choose the mode that works best.' },
  { q: 'What is the batch size for CBSE classes?', a: 'We maintain small batch sizes of 8-10 students to ensure every student receives personalized attention.' },
  { q: 'Do you help with JEE and NEET preparation alongside CBSE?', a: 'Absolutely! We provide focused JEE and NEET preparation integrated with the CBSE curriculum for Grade 11 and 12 students.' },
  { q: 'How much do CBSE classes cost at Planet Gyaan?', a: 'Please contact us via WhatsApp at 9930010800 or call us for the latest fee structure. We offer competitive pricing for the quality of education provided.' },
  { q: 'Is there a free demo class available?', a: 'Yes, we offer a free demo class so you can experience our teaching methodology before enrolling. WhatsApp us to book yours!' },
  { q: 'What results have your CBSE students achieved?', a: 'Our students consistently score above 95% in CBSE board exams. Many have also cracked JEE and NEET with top ranks.' },
];

const CBSEClasses = () => (
  <>
    <Helmet>
      <title>Best CBSE Classes in Santacruz West | Math & Science Coaching | Planet Gyaan</title>
      <meta name="description" content="Looking for CBSE classes in Santacruz West, Mumbai? Planet Gyaan offers expert Math & Science coaching for Grades 8-12. Small batches, experienced faculty, 95%+ results. Call 9930010800." />
      <meta name="keywords" content="CBSE classes Santacruz West, CBSE coaching Santacruz, math tuition Santacruz West Mumbai, science classes Santacruz, best coaching Santacruz West, CBSE tuition near me, Planet Gyaan" />
      <meta property="og:title" content="Best CBSE Classes in Santacruz West | Planet Gyaan" />
      <meta property="og:description" content="Expert CBSE Math & Science coaching for Grades 8-12. Small batches, 95%+ results. Santacruz West, Mumbai." />
      <meta property="og:image" content={LOGO} />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://planetgyaan.com/cbse-classes-santacruz-west" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "Planet Gyaan",
          "description": "CBSE Math & Science coaching institute in Santacruz West, Mumbai. Grades 8-12, JEE & NEET preparation.",
          "url": "https://planetgyaan.com",
          "telephone": "+919930010800",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Ground floor, Dastoor Store, Tirupati Shopping Center, 8, Swami Vivekanand Rd",
            "addressLocality": "Santacruz West",
            "addressRegion": "Mumbai",
            "postalCode": "400054",
            "addressCountry": "IN"
          },
          "foundingDate": "2013",
          "areaServed": "Santacruz West, Mumbai",
          "sameAs": ["https://wa.me/919930010800"]
        }
      `}</script>
    </Helmet>

    {/* HERO */}
    <section data-testid="cbse-hero" className="relative min-h-[85vh] bg-[#18181B] flex items-center overflow-hidden noise-bg">
      <div className="relative z-10 container mx-auto px-6 lg:px-16 pt-28 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 mb-6"
            >
              <MapPin className="w-4 h-4 text-[#F2C402]" />
              <span className="text-[#F2C402] font-bold uppercase tracking-[0.15em] text-xs font-body">Santacruz West, Mumbai</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]"
            >
              <span className="text-white">Best </span>
              <span className="text-[#F2C402]">CBSE Classes</span>
              <span className="text-white"> in Santacruz West</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
              className="text-white/60 text-base md:text-lg mt-6 max-w-lg font-body leading-relaxed"
            >
              Planet Gyaan has been helping CBSE students in Santacruz West excel in Mathematics and Science since 2013. Grades 8-12, small batches, 95%+ results.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
              className="flex flex-wrap gap-4 mt-8"
            >
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="cbse-whatsapp-cta"
                className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:brightness-110 transition-all">
                <WaIcon /> WhatsApp Us
              </a>
              <a href={TEL} data-testid="cbse-call-cta"
                className="flex items-center gap-2 bg-[#F2C402] text-[#0A0A0A] px-6 py-3.5 rounded-xl font-bold text-sm hover:bg-[#E9811F] hover:text-white transition-all">
                <Phone className="w-4 h-4" /> Call 9930010800
              </a>
            </motion.div>
          </div>
          <div className="hidden lg:block">
            <LeadForm variant="dark" />
          </div>
        </div>
      </div>
    </section>

    {/* HIGHLIGHTS */}
    <section data-testid="cbse-highlights" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#121212] tracking-tight">
            Why Choose Planet Gyaan for <span className="text-[#F2C402]">CBSE Classes?</span>
          </h2>
          <p className="text-[#52525B] text-base md:text-lg mt-4 font-body max-w-xl mx-auto">
            Located in the heart of Santacruz West, we've been the go-to coaching institute for CBSE students since 2013.
          </p>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {highlights.map((h, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
              className="brutalist-card rounded-2xl p-6 bg-white"
            >
              <div className="w-10 h-10 rounded-lg bg-[#F2C402]/10 flex items-center justify-center mb-4">
                <h.icon className="w-5 h-5 text-[#F2C402]" />
              </div>
              <h3 className="font-sans font-bold text-[#121212] text-base mb-2">{h.title}</h3>
              <p className="text-[#52525B] text-sm font-body leading-relaxed">{h.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* SUBJECTS & GRADES */}
    <section data-testid="cbse-subjects" className="py-20 md:py-28 bg-[#FAFAFA]">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight">
            CBSE Subjects We <span className="text-[#F2C402]">Teach</span>
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {[
            { subject: 'Mathematics', topics: ['Algebra & Polynomials', 'Trigonometry & Geometry', 'Calculus & Statistics', 'Number Theory', 'Coordinate Geometry'] },
            { subject: 'Science', topics: ['Physics - Mechanics, Optics, Electricity', 'Chemistry - Organic, Inorganic, Physical', 'Biology - Botany, Zoology, Genetics', 'Environmental Science', 'Practical Lab Guidance'] },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="brutalist-card rounded-2xl p-6 md:p-8 bg-white"
            >
              <h3 className="font-sans font-bold text-[#121212] text-xl mb-4 border-b-2 border-[#F2C402] pb-2 inline-block">{s.subject}</h3>
              <ul className="space-y-2 mt-4">
                {s.topics.map((t, j) => (
                  <li key={j} className="flex items-start gap-2 text-[#52525B] text-sm font-body">
                    <CheckCircle2 className="w-4 h-4 text-[#F2C402] shrink-0 mt-0.5" />
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-3 mt-10">
          {['Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'].map((g, i) => (
            <span key={i} className="px-5 py-2.5 bg-white border-2 border-[#121212] rounded-full text-sm font-bold font-body shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">{g}</span>
          ))}
        </div>
      </div>
    </section>

    {/* TESTIMONIALS */}
    <section data-testid="cbse-testimonials" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight">
            What CBSE Students in <span className="text-[#F2C402]">Santacruz Say</span>
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
              data-testid={`cbse-testimonial-${i}`}
              className="bg-[#FAFAFA] rounded-2xl p-6 border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#F2C402] text-[#F2C402]" />)}
              </div>
              <p className="text-[#121212] text-sm font-body leading-relaxed mb-4">"{t.text}"</p>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div>
                  <span className="block font-sans font-bold text-[#121212] text-sm">{t.name}</span>
                  <span className="block text-[#52525B] text-xs font-body">{t.grade}</span>
                </div>
                <span className="text-[#F2C402] font-black text-lg font-sans">{t.score}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ (SEO-optimized) */}
    <section data-testid="cbse-faq" className="py-20 md:py-28 bg-[#FAFAFA]">
      <div className="container mx-auto px-6 lg:px-16 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-black text-[#121212] tracking-tight">
            CBSE Classes in Santacruz West - <span className="text-[#F2C402]">FAQs</span>
          </h2>
        </motion.div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="cbse-faq-accordion">
          {faqs.map((faq, i) => (
            <AccordionItem
              key={i} value={`faq-${i}`}
              data-testid={`cbse-faq-item-${i}`}
              className="bg-white rounded-xl border-2 border-[#121212] px-6 overflow-hidden"
            >
              <AccordionTrigger className="text-[#121212] font-sans font-bold text-sm md:text-base hover:no-underline py-5">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#52525B] font-body text-sm leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    {/* CTA + FORM */}
    <section data-testid="cbse-cta" className="py-16 md:py-24 bg-[#F2C402] border-y-2 border-[#121212]">
      <div className="container mx-auto px-6 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#0A0A0A] tracking-tight">
              Join the Best CBSE Classes in Santacruz West
            </h2>
            <p className="text-[#0A0A0A]/70 text-base md:text-lg mt-4 font-body max-w-lg">
              Enroll today and experience the Planet Gyaan difference. Free demo class available!
            </p>
            <div className="flex flex-wrap gap-4 mt-8 justify-center lg:justify-start">
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="cbse-cta-whatsapp"
                className="flex items-center gap-2 bg-[#0A0A0A] text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-[#121212] transition-all">
                <WaIcon /> WhatsApp Us
              </a>
              <a href={TEL} data-testid="cbse-cta-call"
                className="flex items-center gap-2 bg-white text-[#0A0A0A] px-8 py-4 rounded-xl font-bold text-sm border-2 border-[#121212] shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>
            <div className="flex items-center gap-2 mt-6 text-[#0A0A0A]/60 text-sm font-body justify-center lg:justify-start">
              <MapPin className="w-4 h-4" />
              <span>Tirupati Shopping Center, Swami Vivekanand Rd, Santacruz West</span>
            </div>
            <Link to="/" data-testid="cbse-back-home" className="inline-flex items-center gap-1 text-[#0A0A0A]/60 text-sm font-body mt-4 hover:text-[#0A0A0A] transition-colors">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Home
            </Link>
          </div>
          <div className="lg:block hidden">
            <LeadForm />
          </div>
        </div>
        <div className="lg:hidden mt-10">
          <LeadForm />
        </div>
      </div>
    </section>
  </>
);

export default CBSEClasses;
